import React, { useMemo } from 'react';
import { 
  BarChart3, 
  ArrowUpRight, 
  ArrowDownLeft, 
  Clock, 
  CheckCircle2, 
  AlertCircle,
  PieChart as PieChartIcon
} from 'lucide-react';
import { motion } from 'motion/react';
import { ManagedDocument } from '../../types';

interface CreditReportsProps {
  documents: ManagedDocument[];
}

export const CreditReports: React.FC<CreditReportsProps> = ({ documents }) => {
  const creditDocs = documents.filter(d => d.condition === 'CREDITO');

  const stats = useMemo(() => {
    const totalCredit = creditDocs.reduce((acc, curr) => acc + curr.totalAmount, 0);
    const totalRemaining = creditDocs.reduce((acc, curr) => acc + (curr.remainingAmount || 0), 0);
    const totalPaid = totalCredit - totalRemaining;
    
    const salesCredit = creditDocs.filter(d => d.type === 'SALES_INVOICE' || d.type === 'INTERNAL_INCOME');
    const purchasesCredit = creditDocs.filter(d => d.type === 'PURCHASE_INVOICE' || d.type === 'INTERNAL_EXPENSE');
    
    const pendingSales = salesCredit.reduce((acc, curr) => acc + (curr.remainingAmount || 0), 0);
    const pendingPurchases = purchasesCredit.reduce((acc, curr) => acc + (curr.remainingAmount || 0), 0);

    return {
      totalCredit,
      totalRemaining,
      totalPaid,
      pendingSales,
      pendingPurchases,
      salesCredit,
      purchasesCredit,
      countPending: creditDocs.filter(d => !d.isCancelled).length,
      countCancelled: creditDocs.filter(d => d.isCancelled).length
    };
  }, [creditDocs]);

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('es-PY', { style: 'currency', currency: 'PYG' }).format(amount);
  };

  return (
    <div className="space-y-8">
      {/* KPI Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="bg-zinc-900 border border-zinc-800 p-6 rounded-3xl">
          <div className="flex items-center gap-3 mb-4">
            <div className="p-2 bg-emerald-500/10 rounded-xl border border-emerald-500/20">
              <ArrowUpRight className="text-emerald-500" size={20} />
            </div>
            <span className="text-[10px] font-bold text-zinc-500 uppercase tracking-widest">Cuentas a Cobrar</span>
          </div>
          <div className="text-2xl font-black text-white">{formatCurrency(stats.pendingSales)}</div>
          <p className="text-[10px] text-zinc-500 mt-2 font-medium">DOCUMENTOS DE VENTA PENDIENTES</p>
        </motion.div>

        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0, transition: { delay: 0.1 } }} className="bg-zinc-900 border border-zinc-800 p-6 rounded-3xl">
          <div className="flex items-center gap-3 mb-4">
            <div className="p-2 bg-red-500/10 rounded-xl border border-red-500/20">
              <ArrowDownLeft className="text-red-500" size={20} />
            </div>
            <span className="text-[10px] font-bold text-zinc-500 uppercase tracking-widest">Cuentas a Pagar</span>
          </div>
          <div className="text-2xl font-black text-white">{formatCurrency(stats.pendingPurchases)}</div>
          <p className="text-[10px] text-zinc-500 mt-2 font-medium">DOCUMENTOS DE COMPRA PENDIENTES</p>
        </motion.div>

        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0, transition: { delay: 0.2 } }} className="bg-zinc-900 border border-zinc-800 p-6 rounded-3xl">
          <div className="flex items-center gap-3 mb-4">
            <div className="p-2 bg-amber-500/10 rounded-xl border border-amber-500/20">
              <Clock className="text-amber-500" size={20} />
            </div>
            <span className="text-[10px] font-bold text-zinc-500 uppercase tracking-widest">Doc. Pendientes</span>
          </div>
          <div className="text-2xl font-black text-white">{stats.countPending}</div>
          <p className="text-[10px] text-zinc-500 mt-2 font-medium">CANTIDAD DE FACTURAS A CRÉDITO</p>
        </motion.div>

        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0, transition: { delay: 0.3 } }} className="bg-zinc-900 border border-zinc-800 p-6 rounded-3xl">
          <div className="flex items-center gap-3 mb-4">
            <div className="p-2 bg-indigo-500/10 rounded-xl border border-indigo-500/20">
              <CheckCircle2 className="text-indigo-500" size={20} />
            </div>
            <span className="text-[10px] font-bold text-zinc-500 uppercase tracking-widest">Doc. Liquidados</span>
          </div>
          <div className="text-2xl font-black text-white">{stats.countCancelled}</div>
          <p className="text-[10px] text-zinc-500 mt-2 font-medium">DOCUMENTOS TOTALMENTE PAGADOS</p>
        </motion.div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
        {/* Pending Sales Table */}
        <div className="bg-zinc-900 border border-zinc-800 rounded-3xl overflow-hidden shadow-xl">
                <div className="p-6 border-b border-zinc-800 bg-zinc-800/20 flex items-center justify-between">
              <h3 className="font-bold text-emerald-500 flex items-center gap-2">
                <ArrowUpRight size={18} /> Cuentas a Cobrar (Ventas)
              </h3>
              <span className="text-xs text-zinc-500 bg-zinc-800 px-2 py-1 rounded-md">{stats.salesCredit.filter(d => !d.isCancelled).length} Pendientes</span>
           </div>
           <div className="max-h-[400px] overflow-y-auto custom-scrollbar">
             {stats.salesCredit.filter(d => !d.isCancelled).map(doc => (
               <div key={doc.id} className="p-4 border-b border-zinc-800 last:border-0 flex items-center justify-between hover:bg-zinc-800/30 transition-colors">
                  <div>
                    <div className="text-sm font-bold text-white">{doc.contactName}</div>
                    <div className="text-[10px] text-zinc-500">Factura {doc.number} • {doc.date}</div>
                  </div>
                  <div className="text-right">
                    <div className="text-sm font-bold text-white">{formatCurrency(doc.remainingAmount || 0)}</div>
                    <div className="text-[9px] text-zinc-600">DE {formatCurrency(doc.totalAmount)}</div>
                  </div>
               </div>
             ))}
             {stats.salesCredit.filter(d => !d.isCancelled).length === 0 && (
               <div className="p-12 text-center text-zinc-500 text-sm">No hay saldos pendientes por cobrar.</div>
             )}
           </div>
        </div>

        {/* Pending Purchases Table */}
        <div className="bg-zinc-900 border border-zinc-800 rounded-3xl overflow-hidden shadow-xl">
           <div className="p-6 border-b border-zinc-800 bg-zinc-800/20 flex items-center justify-between">
              <h3 className="font-bold text-red-500 flex items-center gap-2">
                <ArrowDownLeft size={18} /> Cuentas a Pagar (Compras)
              </h3>
              <span className="text-xs text-zinc-500 bg-zinc-800 px-2 py-1 rounded-md">{stats.purchasesCredit.filter(d => !d.isCancelled).length} Pendientes</span>
           </div>
           <div className="max-h-[400px] overflow-y-auto custom-scrollbar">
             {stats.purchasesCredit.filter(d => !d.isCancelled).map(doc => (
               <div key={doc.id} className="p-4 border-b border-zinc-800 last:border-0 flex items-center justify-between hover:bg-zinc-800/30 transition-colors">
                  <div>
                    <div className="text-sm font-bold text-white">{doc.contactName}</div>
                    <div className="text-[10px] text-zinc-500">Factura {doc.number} • {doc.date}</div>
                  </div>
                  <div className="text-right">
                    <div className="text-sm font-bold text-white">{formatCurrency(doc.remainingAmount || 0)}</div>
                    <div className="text-[9px] text-zinc-600">DE {formatCurrency(doc.totalAmount)}</div>
                  </div>
               </div>
             ))}
             {stats.purchasesCredit.filter(d => !d.isCancelled).length === 0 && (
               <div className="p-12 text-center text-zinc-500 text-sm">No hay saldos pendientes por pagar.</div>
             )}
           </div>
        </div>
      </div>
    </div>
  );
};
